import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  Card,
  CardContent,
  Chip,
  useMediaQuery,
  LinearProgress,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useQuery, useMutation, gql } from "@apollo/client";
import Nav from "../common/Nav.jsx";
import FAQSection from "../common/FAQSection.jsx";
import { GRAPHQL_ENDPOINTS } from '../../config/api';
import { BUSINESS_CONSTANTS } from '../../config/businessConstants';
import { getAuthToken, getCsrfTokenSync } from '../../utils/auth';
import { getCsrfTokenKeyByEnv } from '../../utils/csrf';
import { apiFetch } from '../../utils/api';
import { getStatusDisplay } from '../../utils/statusUtils';
import { toast } from 'react-toastify';

const GET_REASONS = gql`
  query fetchReasons {
    fetchReasons {
      status
      message
      data {
        id
        reason
      }
    }
  }
`;

const GET_ISSUES = gql`
  query fetchIssues($input: FetchIssuesFilter!) {
    fetchIssues(filter: $input) {
      status
      message
      data {
        id
        issue
      }
    }
  }
`;

const CREATE_GRIEVANCE_WITH_DOCUMENTS = gql`
  mutation createTicketWithDocuments($input: CreateTicketWithDocumentsInput!) {
    createTicketWithDocuments(input: $input) {
      status
      message
      errorCode
      data {
        ticketId
        statusMsg
        commTypeId
        refType
        documentDisplayIds
      }
    }
  }
`;

const CREATE_TICKET_WITH_DOCUMENTS_QUERY = `mutation createTicketWithDocuments($input: CreateTicketWithDocumentsInput!) {
  createTicketWithDocuments(input: $input) {
    status
    message
    errorCode
    data {
      ticketId
      statusMsg
      commTypeId
      refType
      documentDisplayIds
    }
  }
}`;

const TicketForm = () => {
  const csrfTokenKey = getCsrfTokenKeyByEnv();
  const navigate = useNavigate();
  const location = useLocation();
  const transaction = location.state?.transaction;
  const existingTicketId = transaction?.ticketId;
  const hasExistingTicket =
    existingTicketId != null && String(existingTicketId).trim() !== '';

  const [reasonId, setReasonId] = useState("");
  const [issueId, setIssueId] = useState("");
  const [complaintId, setComplaintId] = useState("");
  const [description, setDescription] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileUploadStatus, setFileUploadStatus] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  // Remove previewImageUrl state and dialog logic
  const [previewFileUrl, setPreviewFileUrl] = useState(null);
  const [previewFileType, setPreviewFileType] = useState(null);

  const [reasonError, setReasonError] = useState(false);
  const [issueError, setIssueError] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [mobileError, setMobileError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);

  // Helper text states for better error messages
  const [nameHelperText, setNameHelperText] = useState("");
  const [emailHelperText, setEmailHelperText] = useState("");
  const [mobileHelperText, setMobileHelperText] = useState("");
  const [descriptionHelperText, setDescriptionHelperText] = useState("");

  // Loading state for navigation
  const [isNavigating, setIsNavigating] = useState(false);
  const [uploadSectionError, setUploadSectionError] = useState("");
  const [apiError, setApiError] = useState("");
  const fileInputRef = useRef(null);

  const resetFileInput = () => {
    // Clear both .value and .files via re-querying so the SAME file can be
    // re-selected (some browsers won't refire onChange otherwise).
    if (fileInputRef.current) {
      try {
        fileInputRef.current.value = '';
      } catch (_) {
        /* ignore */
      }
    }
  };
  const [submitSectionError, setSubmitSectionError] = useState("");

  // Prevent body scrolling when overlay is active
  useEffect(() => {
    if (isNavigating) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isNavigating]);

  // Prevent background scroll when image dialog is open
  useEffect(() => {
    // This useEffect is no longer needed as previewImageUrl is removed
    // if (previewImageUrl) {
    //   document.body.style.overflow = 'hidden';
    // } else {
    //   document.body.style.overflow = 'unset';
    // }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []); // Removed previewImageUrl from dependency array

  // Prevent background scroll when overlay is open
  useEffect(() => {
    if (previewFileUrl) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [previewFileUrl]);

  // Test toast on component mount
  useEffect(() => {
    console.log("Testing toast notification...");
  }, []);

  const {
    data: reasonsData,
    loading: reasonsLoading,
    error: reasonsError,
  } = useQuery(GET_REASONS);

  const {
    data: issuesData,
    loading: issuesLoading,
    error: issuesError,
  } = useQuery(GET_ISSUES, {
    variables: { input: { reasonId: parseInt(reasonId) } },
    skip: !reasonId,
  });

  // createTicketWithDocuments is invoked directly via multipart fetch
  // (Apollo Client is not configured with apollo-upload-client here).
  const mutationLoading = false;
  const mutationError = null;

  // Handle GraphQL query errors with toast notifications
  React.useEffect(() => {
    if (reasonsError) {
      const errorMsg = reasonsError.message || "Failed to load reasons";
      console.error(errorMsg);
    }
  }, [reasonsError]);

  React.useEffect(() => {
    if (issuesError) {
      const errorMsg = issuesError.message || "Failed to load issues";
      console.error(errorMsg);
    }
  }, [issuesError]);

  React.useEffect(() => {
    if (mutationError) {
      const errorMsg = mutationError.message || "Failed to create ticket. Please try again later";
      console.error(errorMsg);
    }
  }, [mutationError]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);

    if (files.length === 0) return;

    const maxFiles = BUSINESS_CONSTANTS.FILE_UPLOAD.MAX_FILES;
    const maxMb = Math.round(BUSINESS_CONSTANTS.FILE_UPLOAD.MAX_FILE_SIZE / (1024 * 1024));
    const remainingSlots = maxFiles - selectedFiles.length;

    if (remainingSlots <= 0) {
      setUploadSectionError(`You can upload a maximum of ${maxFiles} files.`);
      resetFileInput();
      return;
    }

    const accepted = [];
    const errors = [];
    const maxFilesExceededMsg = `Only ${maxFiles} files allowed. Extra files were ignored.`;

    for (const file of files) {
      if (accepted.length >= remainingSlots) {
        errors.push(maxFilesExceededMsg);
        break;
      }
      if (file.size > BUSINESS_CONSTANTS.FILE_UPLOAD.MAX_FILE_SIZE) {
        errors.push(`"${file.name}" exceeds ${maxMb} MB.`);
        continue;
      }
      if (!BUSINESS_CONSTANTS.FILE_UPLOAD.SUPPORTED_TYPES.includes(file.type)) {
        errors.push(`"${file.name}" has unsupported file type.`);
        continue;
      }
      accepted.push({
        id: Date.now() + Math.random(),
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        uploadStatus: 'ready',
        uploadProgress: 0,
        uploadUrl: null,
      });
    }

    if (accepted.length > 0) {
      setSelectedFiles(prev => [...prev, ...accepted]);
      setFileUploadStatus(prev => {
        const next = { ...prev };
        accepted.forEach(f => { next[f.id] = 'ready'; });
        return next;
      });
    }

    setUploadSectionError(errors.length > 0 ? errors.join(' ') : '');
    if (errors.includes(maxFilesExceededMsg)) {
      toast.error(maxFilesExceededMsg);
    }
    setApiError('');
    resetFileInput();
  };

  const removeFile = (fileId) => {
    setSelectedFiles(prev => prev.filter(f => f.id !== fileId));
    setFileUploadStatus(prev => {
      const next = { ...prev };
      delete next[fileId];
      return next;
    });
    setUploadProgress(prev => {
      const next = { ...prev };
      delete next[fileId];
      return next;
    });
    setUploadSectionError("");
  };

  // Legacy two-step upload kept as a no-op shim; createTicketWithDocuments
  // now uploads files inline as part of the ticket submission.
  const uploadFile = async () => {};

  const _legacyUploadFileUnused = async (fileObj) => {
    const { id, file } = fileObj;
    
    console.log(`Starting upload for file: ${fileObj.name}`, fileObj);
    
    // Update status to uploading
    setFileUploadStatus(prev => ({ ...prev, [id]: 'uploading' }));
    
    try {
      console.log(`Making file upload call for: ${fileObj.name}`);
      
      // Create FormData for file upload matching the curl structure
      const formData = new FormData();
      formData.append('operations', JSON.stringify({
        query: `mutation UploadTicket($files: [Upload]!) { 
          uploadTicket(files: $files) { 
            status 
            message 
            data { 
              files { 
                fileName 
                fileUrl 
                uploadStatus 
              } 
              totalFiles 
              totalSize 
            } 
          } 
        }`,
        variables: {
          files: [null] // Placeholder for file
        }
      }));
      
      formData.append('map', JSON.stringify({
        "0": ["variables.files.0"]
      }));
      
      formData.append('0', file);
      
      const response = await apiFetch(GRAPHQL_ENDPOINTS.GRAPHQL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken') || ''}`,
          [csrfTokenKey]: getCsrfTokenSync()
          // Don't set Content-Type for FormData - browser will set it automatically with boundary
        },
        credentials: 'include', // Include cookies in the request
        body: formData,
      });
      
      const data = await response.json();
      console.log(`Upload response for ${fileObj.name}:`, data);
      
      if (data.data?.uploadTicket?.status === "SUCCESS" || data.data?.uploadTicket?.status === "OK") {
        const uploadedFile = data.data.uploadTicket.data.files[0];
        
        // Update status to success
        setFileUploadStatus(prev => ({ ...prev, [id]: 'success' }));
        setUploadProgress(prev => ({ ...prev, [id]: 100 }));
        
        // Update file object with server response
        setSelectedFiles(prev => prev.map(f => 
          f.id === id 
            ? { 
                ...f, 
                serverId: uploadedFile.fileName,
                uploadUrl: uploadedFile.fileUrl,
                uploadStatus: uploadedFile.uploadStatus,
                type: f.type // Ensure type is preserved for image preview
              }
            : f
        ));
        
        console.log(`File ${fileObj.name} uploaded successfully:`, uploadedFile);
        
        // Show success toast with statusMsg
        const statusMsg = data.data.uploadTicket.data?.statusMsg || data.data.uploadTicket.message || `File ${fileObj.name} uploaded successfully`;
        console.log(statusMsg);
        setUploadSectionError("");
      } else {
        throw new Error(data.errors?.[0]?.message || data.data?.uploadTicket?.message || 'Upload failed');
      }
    } catch (error) {
      console.error(`Error uploading file ${fileObj.name}:`, error);
      
      // Update status to error
      setFileUploadStatus(prev => ({ ...prev, [id]: 'error' }));
      
      const msg =
        error instanceof Error ? error.message : "Upload failed. Please try again.";
      setUploadSectionError(msg);
    }
  };

  // No-op kept for backwards compatibility; uploads now happen inline.
  const uploadAllFiles = async () => [];

  const validateEmail = (val) => {
    if (!val.trim()) return { isValid: false, message: "Email is required" };
    
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(val.trim())) {
      return { isValid: false, message: "Please enter a valid email address" };
    }
    
    // Check for common invalid patterns
    if (val.includes('..') || val.startsWith('.') || val.endsWith('.')) {
      return { isValid: false, message: "Please enter a valid email address" };
    }
    
    return { isValid: true, message: "" };
  };

  const validatePhone = (val) => {
    if (!val.trim()) return { isValid: false, message: "Mobile number is required" };
    
    // Remove any spaces, dashes, or parentheses
    const cleanNumber = val.replace(/[\s\-\(\)]/g, '');
    
    // Check if it contains only digits
    if (!/^\d+$/.test(cleanNumber)) {
      return { isValid: false, message: "Mobile number should contain only digits" };
    }
    
    // For Indian mobile numbers, restrict to exactly 10 digits
    if (cleanNumber.length !== 10) {
      return { isValid: false, message: "Mobile number should be exactly 10 digits" };
    }
    
    // For Indian numbers, should start with 6-9
    if (!/^[6-9]/.test(cleanNumber)) {
      return { isValid: false, message: "Please enter a valid Indian mobile number starting with 6, 7, 8, or 9" };
    }
    
    return { isValid: true, message: "" };
  };

  const validateName = (val) => {
    if (!val.trim()) return { isValid: false, message: "Name is required" };
    
    const trimmedName = val.trim();
    
    // Check minimum length
    if (trimmedName.length < 2) {
      return { isValid: false, message: "Name should be at least 2 characters long" };
    }
    
    // Check maximum length
    if (trimmedName.length > 50) {
      return { isValid: false, message: "Name should not exceed 50 characters" };
    }
    
    // Check for consecutive spaces
    if (trimmedName.includes('  ')) {
      return { isValid: false, message: "Name should not contain consecutive spaces" };
    }
    
    // Check for special characters - only allow letters, spaces, dots, hyphens, and apostrophes
    const namePattern = /^[a-zA-Z\s\.\-']+$/;
    if (!namePattern.test(trimmedName)) {
      return { isValid: false, message: "Name should only contain letters, spaces, dots, hyphens, and apostrophes" };
    }
    
    // Check for numbers
    if (/\d/.test(trimmedName)) {
      return { isValid: false, message: "Name should not contain numbers" };
    }
    
    // Check for multiple consecutive special characters
    if (/[\.\-']{2,}/.test(trimmedName)) {
      return { isValid: false, message: "Name should not contain consecutive special characters" };
    }
    
    // Check for names that start or end with special characters (except apostrophe for names like O'Connor)
    if (/^[\.\-]/.test(trimmedName) || /[\.\-]$/.test(trimmedName)) {
      return { isValid: false, message: "Name should not start or end with dots or hyphens" };
    }
    
    // Check for names with only special characters
    if (/^[\.\-'\s]+$/.test(trimmedName)) {
      return { isValid: false, message: "Name should contain at least one letter" };
    }
    
    // Check for common invalid patterns
    if (trimmedName.toLowerCase() === 'test' || trimmedName.toLowerCase() === 'admin' || trimmedName.toLowerCase() === 'user') {
      return { isValid: false, message: "Please enter a valid name" };
    }
    
    return { isValid: true, message: "" };
  };

  const validateDescription = (val) => {
    if (!val.trim()) {
      return { isValid: false, message: "Description is required" };
    }
    if (val.length > BUSINESS_CONSTANTS.TICKET.MAX_DESCRIPTION_LENGTH) {
      return {
        isValid: false,
        message: `Description should not exceed ${BUSINESS_CONSTANTS.TICKET.MAX_DESCRIPTION_LENGTH} characters`,
      };
    }
    return { isValid: true, message: "" };
  };

  // Real-time validation handlers
  const handleNameChange = (e) => {
    const value = e.target.value;
    const namePattern = /^[a-zA-Z\s\.\-']*$/;

    if (!namePattern.test(value) && value !== '') {
      setNameError(true);
      setNameHelperText("Name should only contain letters, spaces, dots, hyphens, and apostrophes");
      return;
    }

    setName(value);

    if (value.trim()) {
      const validation = validateName(value);
      setNameError(!validation.isValid);
      setNameHelperText(validation.message);
    } else {
      setNameError(false);
      setNameHelperText("");
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value.toLowerCase();
    setEmail(value);
    
    if (value.trim()) {
      const validation = validateEmail(value);
      setEmailError(!validation.isValid);
      setEmailHelperText(validation.message);
    } else {
      setEmailError(false);
      setEmailHelperText("");
    }
  };

  const handleMobileChange = (e) => {
    const value = e.target.value;
    
    // Only allow digits, spaces, dashes, and parentheses
    const phonePattern = /^[\d\s\-\(\)]*$/;
    if (phonePattern.test(value) || value === '') {
      setMobile(value);
      
      if (value.trim()) {
        const validation = validatePhone(value);
        setMobileError(!validation.isValid);
        setMobileHelperText(validation.message);
      } else {
        setMobileError(false);
        setMobileHelperText("");
      }
    }
    // If invalid character is entered, don't update the state (prevent input)
  };

  const COMPLAINT_ID_MAX_LENGTH = 50;

  const handleComplaintIdChange = (e) => {
    const value = e.target.value;
    if (value.length <= COMPLAINT_ID_MAX_LENGTH) {
      setComplaintId(value);
    }
  };

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    if (value.length > BUSINESS_CONSTANTS.TICKET.MAX_DESCRIPTION_LENGTH) {
      return;
    }
    setDescription(value);

    if (value.trim()) {
      const validation = validateDescription(value);
      setDescriptionError(!validation.isValid);
      setDescriptionHelperText(validation.message);
    } else {
      setDescriptionError(false);
      setDescriptionHelperText("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setReasonError(false);
    setIssueError(false);
    setNameError(false);
    setEmailError(false);
    setMobileError(false);
    setDescriptionError(false);
    
    // Reset helper text
    setNameHelperText("");
    setEmailHelperText("");
    setMobileHelperText("");
    setDescriptionHelperText("");
    setSubmitSectionError(""); // Reset submit errors before validation / submit

    let hasError = false;

    if (!reasonId) {
      setReasonError(true);
      hasError = true;
    }
    if (!issueId) {
      setIssueError(true);
      hasError = true;
    }
    
    // Validate name
    const nameValidation = validateName(name);
    if (!nameValidation.isValid) {
      setNameError(true);
      setNameHelperText(nameValidation.message);
      hasError = true;
    }
    
    // Validate email
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      setEmailError(true);
      setEmailHelperText(emailValidation.message);
      hasError = true;
    }
    
    // Validate mobile
    const mobileValidation = validatePhone(mobile);
    if (!mobileValidation.isValid) {
      setMobileError(true);
      setMobileHelperText(mobileValidation.message);
      hasError = true;
    }

    const descriptionValidation = validateDescription(description);
    if (!descriptionValidation.isValid) {
      setDescriptionError(true);
      setDescriptionHelperText(descriptionValidation.message);
      hasError = true;
    }

    if (hasError) return;

    // Show loader immediately when submission starts
    setIsNavigating(true);

    try {
      // Helper function to safely convert to integer
      const safeParseInt = (value, defaultValue = 0) => {
        if (value === null || value === undefined || value === '') {
          return defaultValue;
        }
        const parsed = parseInt(value);
        return isNaN(parsed) ? defaultValue : parsed;
      };

      const filesToSend = selectedFiles;
      const hasDocument = filesToSend.length > 0;

      // Build input for createTicketWithDocuments. The schema expects
      // { ticket: CreateTicketWithDocumentsRequest!, files: [Upload] }.
      // When files are attached we send `files: [null, null, ...]` placeholders
      // per the GraphQL multipart spec; the actual files are mapped via the
      // FormData `map` field.
      const ticket = {
        mobileNo: mobile.trim(),
        email: email.trim(),
        name: name.trim(),
        issueId: safeParseInt(issueId, 0),
        refType: safeParseInt(BUSINESS_CONSTANTS.TICKET.DEFAULT_REF_TYPE, 1),
        paymentId: transaction?.paymentId,
        commTypeId: safeParseInt(BUSINESS_CONSTANTS.TICKET.DEFAULT_COMM_TYPE_ID, 1),
        description: description.trim(),
        additionalDetails: {
          merchantComplaintId: complaintId.trim() || null
        }
      };

      const input = {
        ticket,
        ...(hasDocument ? { files: filesToSend.map(() => null) } : {})
      };

      // Mark all files as uploading for UI feedback
      if (hasDocument) {
        setFileUploadStatus(prev => {
          const next = { ...prev };
          filesToSend.forEach(f => { next[f.id] = 'uploading'; });
          return next;
        });
      }

      // Build multipart form per graphql-multipart-request-spec
      const formData = new FormData();
      formData.append('operations', JSON.stringify({
        operationName: 'createTicketWithDocuments',
        variables: { input },
        query: CREATE_TICKET_WITH_DOCUMENTS_QUERY
      }));

      if (hasDocument) {
        const map = {};
        filesToSend.forEach((_, idx) => {
          map[String(idx)] = [`variables.input.files.${idx}`];
        });
        formData.append('map', JSON.stringify(map));
        filesToSend.forEach((f, idx) => {
          formData.append(String(idx), f.file);
        });
      } else {
        formData.append('map', JSON.stringify({}));
      }

      // Resolve auth token: prefer sessionStorage bearerToken when present
      // (paymentId deep-link flow), otherwise fall back to standard auth token.
      if (ticket.paymentId) {
        sessionStorage.setItem('bearerToken', sessionStorage.getItem('bearerToken') || '');
      }
      const bearerToken = sessionStorage.getItem('bearerToken') || getAuthToken() || '';

      const response = await apiFetch(GRAPHQL_ENDPOINTS.GRAPHQL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${bearerToken}`,
          [csrfTokenKey]: getCsrfTokenSync(),
          'apollo-require-preflight': 'true',
          'Accept': '*/*'
          // Do NOT set Content-Type; browser will add multipart boundary.
        },
        credentials: 'include',
        body: formData,
      });

      const json = await response.json();

      if (json?.errors) {
        toast.error(json?.errors?.[0]?.message || "Error submitting ticket. Please try again.");
        return;
      }
      const result = json?.data?.createTicketWithDocuments;

      console.log("createTicketWithDocuments response:", json);

      if (!result) {
        console.error("Invalid response structure:", json);
        if (hasDocument) {
          setFileUploadStatus(prev => {
            const next = { ...prev };
            filesToSend.forEach(f => { next[f.id] = 'error'; });
            return next;
          });
        }
        setApiError(json?.errors?.[0]?.message || "Error submitting ticket. Please try again.");
        return;
      }

      const isSuccess = (result.status === "SUCCESS" || result.status === "OK") && result.data;

      if (!isSuccess) {
        console.error("Submission failed:", result.message, "errorCode:", result.errorCode);
        if (hasDocument) {
          setFileUploadStatus(prev => {
            const next = { ...prev };
            filesToSend.forEach(f => { next[f.id] = 'error'; });
            return next;
          });
        }
        setApiError(result.message || "Ticket submission failed");
        return;
      }

      // Success — update file status, then navigate to the ticket page.
      setApiError("");
      if (hasDocument) {
        setFileUploadStatus(prev => {
          const next = { ...prev };
          filesToSend.forEach(f => { next[f.id] = 'success'; });
          return next;
        });
        setUploadProgress(prev => {
          const next = { ...prev };
          filesToSend.forEach(f => { next[f.id] = 100; });
          return next;
        });
        const displayIds = result.data.documentDisplayIds || [];
        if (displayIds.length > 0) {
          setSelectedFiles(prev => prev.map((f, idx) => (
            displayIds[idx] ? { ...f, serverId: displayIds[idx] } : f
          )));
        }
      }

      const ticketId =
        result.data?.ticketId ||
        result.ticketId ||
        result.data?.id;

      if (!ticketId) {
        console.error("Ticket created but missing ticket ID:", result);
        setApiError("Ticket created but missing ticket ID. Please contact support.");
        return;
      }

      setTimeout(() => {
        navigate("/ticket-page", {
          state: {
            ticketId,
            ticketData: result.data,
            email: email.trim(),
            transaction: transaction
          }
        });
      }, BUSINESS_CONSTANTS.TIMEOUTS.NAVIGATION_DELAY);
    } catch (error) {
      console.error("Error submitting form:", error);
      
      // Provide more specific error messages based on the error type
      if (error.message.includes("NetworkError")) {
        console.error("Network error: Please check your internet connection and try again.");
      } else if (error.message.includes("GraphQL error")) {
        console.error("Server error: Please try again later or contact support.");
      } else {
        console.error(`Error submitting ticket: ${error.message}`);
      }
      setSubmitSectionError(error.message || "Error submitting ticket");
    } finally {
      // Hide loader if there was an error
      setIsNavigating(false);
    }
  };

  const isFormComplete =
    Boolean(reasonId) &&
    Boolean(issueId) &&
    validateName(name).isValid &&
    validateEmail(email).isValid &&
    validatePhone(mobile).isValid &&
    validateDescription(description).isValid;

  const logo = "/path-to-blinkit-logo.png";
  const merchant = transaction?.merchantName || "Merchant";
  const status = transaction?.statusMsg || "Success";
  const amount = transaction?.amount || 290;
  const type = transaction?.mode || "UPI";
  const transactionId = transaction?.transactionId || "N/A";
  const dateStr = transaction?.date || "2025-11-18T10:10:00";
  const dateObj = new Date(dateStr);

  // Add a helper function for formatting
  function formatDateTime(dateObj) {
    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    const dd = String(dateObj.getDate()).padStart(2, '0');
    const hh = String(dateObj.getHours()).padStart(2, '0');
    const min = String(dateObj.getMinutes()).padStart(2, '0');
    const ss = String(dateObj.getSeconds()).padStart(2, '0');
    return `${yyyy}/${mm}/${dd} ${hh}:${min}:${ss}`;
  }

  const requiredMark = (
    <Box component="span" sx={{ color: '#DE350B', ml: 0.25 }} aria-hidden="true">
      *
    </Box>
  );

  return (
    <Box
      sx={{
        background: "linear-gradient(to bottom, #E9F7FF, #DDEFFF, #FFFFFF)",
        minHeight: "100vh",
      }}
    >
      {/* CSS Animation for Spinner */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      <Nav />

      <Box
        sx={{
          px: { xs: "16px", sm: "40px", md: "70px" },
          position: 'relative',
          display: 'block',
        }}
      >
        <Grid
          container
          spacing={4}
          sx={{
            maxWidth: "1280px",
            width: "100%",
            margin: '0 auto',
            justifyContent: 'center',
          }}
        >
          <Grid
            item
            xs={12}
            sm={4}
            sx={{ display: { xs: "none", sm: "block" } }}
          >
            <Typography
              sx={{
                mb: 2,
                fontWeight: 700,
                fontSize: "20px",
                lineHeight: "24px",
                color: "#253858",
              }}
            >
              Payment Status
            </Typography>
            <Card
              sx={{
                width: "100%",
                boxShadow: "none",
                borderRadius: "8px",
                position: "relative",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar src={logo} sx={{ width: 48, height: 48, mr: 2 }} />
                    <Typography
                      sx={{ fontSize: "16px", fontWeight: 400, color: "#253858" }}
                    >
                      {merchant}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
                    <Chip
                      label={getStatusDisplay(transaction.status).label}
                      sx={{
                        backgroundColor: getStatusDisplay(transaction.status).backgroundColor,
                        color: getStatusDisplay(transaction.status).color,
                        height: 22,
                        borderRadius: "100px",
                        fontWeight: 700,
                        fontSize: "12px",
                      }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "18px",
                        color: "#000000",
                      }}
                    >
                      ₹{(Number(amount) / 100).toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
              <Box
                sx={{
                  backgroundColor: "#F4F5F7",
                  border: "2px solid #FFFFFF",
                  borderTop: "1px dashed #DFE1E6",
                  px: 2,
                  py: 1,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: "14px",
                    color: "#253858",
                  }}
                >
                  {type} • {transactionId}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: "14px",
                    color: "#253858",
                    opacity: 0.6,
                  }}
                >
                  {formatDateTime(dateObj)}
                </Typography>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} sm={8}>
            {/* Go Back button and title left-aligned, button indented to match form fields */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <IconButton
                onClick={handleGoBack}
                sx={{
                  p: 0,
                  mr: 1,
                  ml: { xs: 0, sm: 0, md: 0, lg: 0 }, // Will update below for form alignment
                }}
              >
                <img src="/Icons/Goback.svg" alt="Go back" />
              </IconButton>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '20px',
                  lineHeight: '24px',
                  color: '#253858',
                  textAlign: 'left',
                }}
              >
                Raise your Concern
              </Typography>
            </Box>

            {hasExistingTicket ? (
              <Box
                sx={{
                  backgroundColor: "white",
                  p: { xs: 2, sm: 4 },
                  borderRadius: "20px",
                  boxShadow: "none",
                  mx: "auto",
                  width: { xs: "100%", md: "717px" },
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    border: "1px solid #DFE1E6",
                    p: 3,
                    borderRadius: "8px",
                    mb: 3,
                    textAlign: "left",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 700, color: "#253858", fontSize: "18px" }}
                  >
                    Ticket already created
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#25385899", mt: 1, lineHeight: "20px" }}
                  >
                    A ticket has already been raised for this transaction (Ticket ID: <strong>{existingTicketId}</strong>). You cannot create another ticket for the same transaction.
                  </Typography>
                </Box>
                <Button
                  onClick={() => navigate('/check-query', { state: { bypassAuth: true } })}
                  fullWidth
                  sx={{
                    py: 1.5,
                    borderRadius: "8px",
                    backgroundColor: "#3965FB",
                    fontSize: "1rem",
                    fontWeight: 500,
                    color: "#ffffff",
                    textTransform: "capitalize",
                    "&:hover": { backgroundColor: "#335ecc" },
                  }}
                >
                  Track Existing Ticket
                </Button>
              </Box>
            ) : (
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                backgroundColor: "white",
                p: 2,
                borderRadius: "20px",
                boxShadow: "none",
                mx: "auto",
                width: { xs: "100%", md: "717px" },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "16px",
                  color: "#253858",
                  mb: 1,
                }}
              >
                Why did you choose to come to PB Pay?{requiredMark}
              </Typography>
              <FormControl fullWidth required sx={{ mb: 2 }} error={reasonError}>
                <InputLabel>Select reason</InputLabel>
                <Select
                  value={reasonId}
                  onChange={(e) => {
                    setReasonId(e.target.value);
                    setIssueId("");
                  }}
                  label="Select reason"
                  required
                  disabled={reasonsLoading}
                >
                  {reasonsData?.fetchReasons?.data?.map((reason) => (
                    <MenuItem key={reason.id} value={reason.id}>
                      {reason.reason}
                    </MenuItem>
                  ))}
                </Select>
                {reasonError && (
                  <FormHelperText>Please select a reason.</FormHelperText>
                )}
                {reasonsError && (
                  <FormHelperText error>
                    Error loading reasons: {reasonsError.message}
                  </FormHelperText>
                )}
              </FormControl>

              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "16px",
                  color: "#253858",
                  mb: 1,
                }}
              >
                What issue are you facing with the transaction?{requiredMark}
              </Typography>
              <FormControl fullWidth required sx={{ mb: 2 }} error={issueError}>
                <InputLabel>Select issue</InputLabel>
                <Select
                  value={issueId}
                  onChange={(e) => setIssueId(e.target.value)}
                  label="Select issue"
                  required
                  disabled={issuesLoading || !reasonId}
                >
                  {issuesData?.fetchIssues?.data?.map((issue) => (
                    <MenuItem key={issue.id} value={issue.id}>
                      {issue.issue}
                    </MenuItem>
                  ))}
                </Select>
                {issueError && (
                  <FormHelperText>Please select an issue.</FormHelperText>
                )}
                {issuesError && (
                  <FormHelperText error>
                    Error loading issues: {issuesError.message}
                  </FormHelperText>
                )}
              </FormControl>

              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "16px",
                  color: "#253858",
                  mb: 1,
                }}
              >
                Please provide Merchant complaint ID
              </Typography>
              <TextField
                label="Enter merchant complaint ID"
                variant="outlined"
                fullWidth
                value={complaintId}
                onChange={handleComplaintIdChange}
                sx={{ mb: 2 }}
              />

              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "16px",
                  color: "#253858",
                  mb: 1,
                }}
              >
                Already connected with Merchant? Please share a screenshot of
                the email communication
              </Typography>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                disabled={selectedFiles.length >= BUSINESS_CONSTANTS.FILE_UPLOAD.MAX_FILES}
                sx={{
                  color: "#0065FF",
                  fontWeight: 500,
                  fontSize: { xs: "14px", sm: "16px" }, // Responsive font size
                  mb: 2,
                  textTransform: "capitalize",
                  border: "1px dashed #4C9AFF",
                  py: { xs: 1.5, sm: 2 }, // Responsive padding
                  px: { xs: 1, sm: 2 }, // Responsive padding
                  minHeight: { xs: "48px", sm: "56px" }, // Responsive height
                  '&:hover': {
                    backgroundColor: 'rgba(0, 101, 255, 0.1)',
                    borderColor: '#0065FF',
                    borderStyle: 'solid',
                  },
                }}
              >
                <CloudUploadIcon sx={{ mr: 1, fontSize: { xs: "20px", sm: "24px" } }} />
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' }, // Stack on mobile
                  alignItems: 'center',
                  gap: { xs: 0.5, sm: 1 },
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}>
                  <span>
                    {selectedFiles.length > 0
                      ? `Add more files (${selectedFiles.length}/${BUSINESS_CONSTANTS.FILE_UPLOAD.MAX_FILES})`
                      : 'Select files to upload'}
                  </span>
                  <Typography
                    component="span"
                    sx={{ fontSize: { xs: "12px", sm: "14px" }, color: "inherit" }}
                  >
                    (Max {Math.round(BUSINESS_CONSTANTS.FILE_UPLOAD.MAX_FILE_SIZE / (1024 * 1024))} MB)
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      width: '100%',
                      textAlign: 'center',
                      fontSize: { xs: '11px', sm: '12px' },
                      color: '#6B778C',
                      fontWeight: 400,
                      lineHeight: 1.4,
                    }}
                  >
                    Accepted: JPG, JPEG, PNG, GIF, PDF, DOC, DOCX, TXT
                  </Typography>
                </Box>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  multiple
                  onChange={handleFileChange}
                  onClick={(e) => {
                    // Always clear before opening picker so re-selecting the
                    // same file (e.g. after an "Unsupported file type" error)
                    // still fires onChange.
                    e.currentTarget.value = '';
                  }}
                  accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.txt"
                />
              </Button>

              {uploadSectionError ? (
                <Typography color="error" sx={{ mb: 2, fontWeight: 500 }}>
                  {uploadSectionError}
                </Typography>
              ) : null}
              
              {/* Display selected file */}
              {selectedFiles.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {selectedFiles.map((fileObj) => (
                    <Box
                      key={fileObj.id}
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" }, // Stack on mobile
                        alignItems: { xs: "flex-start", sm: "center" },
                        justifyContent: "space-between",
                        p: { xs: 1.5, sm: 1 },
                        mb: 1,
                        border: "1px solid #E1E5E9",
                        borderRadius: "8px",
                        backgroundColor: "#F8F9FA",
                        gap: { xs: 1, sm: 0 }, // Add gap on mobile
                      }}
                    >
                      <Box sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        flex: 1,
                        width: { xs: "100%", sm: "auto" } // Full width on mobile
                      }}>
                        <InsertDriveFileIcon sx={{ 
                          width: { xs: 18, sm: 20 }, 
                          height: { xs: 18, sm: 20 }, 
                          marginRight: { xs: 6, sm: 8 },
                          flexShrink: 0
                        }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#253858",
                              fontWeight: 500,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: { xs: "13px", sm: "14px" }, // Smaller font on mobile
                            }}
                          >
                            {fileObj.name.length > 25 ? fileObj.name.slice(0, 25) + '...' : fileObj.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ 
                              color: "#6B778C",
                              fontSize: { xs: "11px", sm: "12px" } // Smaller font on mobile
                            }}
                          >
                            {(fileObj.size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: { xs: 0.5, sm: 1 },
                        width: { xs: "100%", sm: "auto" }, // Full width on mobile
                        justifyContent: { xs: "flex-end", sm: "flex-start" }
                      }}>
                        {/* Upload Status */}
                        {fileUploadStatus[fileObj.id] === 'ready' && (
                          <Chip
                            label="Ready"
                            size="small"
                            sx={{
                              backgroundColor: "#FFF3CD",
                              color: "#856404",
                              fontSize: { xs: "9px", sm: "10px" },
                            }}
                          />
                        )}
                        {fileUploadStatus[fileObj.id] === 'uploading' && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="caption" sx={{ 
                              color: "#0066CC",
                              fontSize: { xs: "9px", sm: "10px" } // Smaller font on mobile
                            }}>
                              {uploadProgress[fileObj.id]}%
                            </Typography>
                            <Chip
                              label="Uploading"
                              size="small"
                              sx={{
                                backgroundColor: "#E3F2FD",
                                color: "#1976D2",
                                fontSize: { xs: "9px", sm: "10px" }, // Smaller font on mobile
                              }}
                            />
                          </Box>
                        )}
                        {fileUploadStatus[fileObj.id] === 'success' && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Chip
                              label="Uploaded"
                              size="small"
                              sx={{
                                backgroundColor: "#D4EDDA",
                                color: "#155724",
                                fontSize: { xs: "9px", sm: "10px" }, // Smaller font on mobile
                              }}
                            />
                            {fileObj.uploadUrl && (
                              <a
                                href={fileObj.uploadUrl}
                                download
                                style={{ textDecoration: 'none' }}
                              >
                                <Button
                                  size="small"
                                  sx={{
                                    minWidth: "auto",
                                    p: { xs: 0.3, sm: 0.5 }, // Smaller padding on mobile
                                    fontSize: { xs: "9px", sm: "10px" }, // Smaller font on mobile
                                    textTransform: "capitalize",
                                    color: "#0065FF",
                                    backgroundColor: "#E8F1FF",
                                    "&:hover": {
                                      backgroundColor: "#D1E7FF"
                                    }
                                  }}
                                >
                                  Download
                                </Button>
                              </a>
                            )}
                          </Box>
                        )}
                        {fileUploadStatus[fileObj.id] === 'error' && (
                          <Chip
                            label="Failed"
                            size="small"
                            sx={{
                              backgroundColor: "#F8D7DA",
                              color: "#721C24",
                              fontSize: { xs: "9px", sm: "10px" },
                            }}
                          />
                        )}
                        
                        {/* Remove button */}
                        <IconButton
                          size="small"
                          onClick={() => removeFile(fileObj.id)}
                          sx={{
                            color: "#DC3545",
                            "&:hover": { backgroundColor: "#F8D7DA" },
                            width: { xs: 28, sm: 32 }, // Smaller on mobile
                            height: { xs: 28, sm: 32 }, // Smaller on mobile
                          }}
                        >
                          <DeleteIcon sx={{ width: { xs: 14, sm: 16 }, height: { xs: 14, sm: 16 } }} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
              
              {/* Legacy single file display for backward compatibility */}
              {selectedFile && !selectedFiles.length && (
                <Typography
                  variant="body2"
                  sx={{ mb: 2, color: "#5E6C84", textAlign: "left" }}
                >
                  Selected file: {selectedFile.name}
                </Typography>
              )}

              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "16px",
                  color: "#253858",
                  mb: 1,
                }}
              >
                Description{requiredMark}
              </Typography>
              <TextField
                multiline
                rows={4}
                required
                placeholder="Write a brief description of your request"
                variant="outlined"
                fullWidth
                value={description}
                onChange={handleDescriptionChange}
                error={descriptionError}
                inputProps={{
                  maxLength: BUSINESS_CONSTANTS.TICKET.MAX_DESCRIPTION_LENGTH,
                }}
                helperText={
                  descriptionError
                    ? descriptionHelperText
                    : `${description.length}/${BUSINESS_CONSTANTS.TICKET.MAX_DESCRIPTION_LENGTH}`
                }
                sx={{ mb: 2 }}
              />

              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "16px",
                  color: "#253858",
                  mb: 1,
                }}
              >
                How do we get in touch with you?
              </Typography>
              <TextField
                required
                label="Your full name"
                placeholder="Enter your name"
                variant="outlined"
                fullWidth
                value={name}
                onChange={handleNameChange}
                error={nameError}
                helperText={nameError ? nameHelperText : ""}
                sx={{ mb: 2 }}
              />
              <TextField
                required
                label="Email address"
                variant="outlined"
                fullWidth
                value={email}
                onChange={handleEmailChange}
                error={emailError}
                helperText={emailError ? emailHelperText : ""}
                sx={{ mb: 2 }}
              />
              <TextField
                required
                label="Mobile number"
                variant="outlined"
                fullWidth
                value={mobile}
                onChange={handleMobileChange}
                error={mobileError}
                helperText={mobileError ? mobileHelperText : ""}
                inputProps={{
                  maxLength: 10,
                  pattern: "[0-9]*",
                  inputMode: "numeric"
                }}
                sx={{ mb: 2 }}
              />

              {submitSectionError ? (
                <Typography color="error" sx={{ mb: 2, fontWeight: 500 }}>
                  {submitSectionError}
                </Typography>
              ) : null}

              <Button
                type="submit"
                disabled={!isFormComplete || mutationLoading || isNavigating}
                sx={{
                  backgroundColor: "#3965FB",
                  color: "white",
                  width: { md: "328px", xs: "100%" },
                  borderRadius: "8px",
                  p: "12px 20px",
                  textTransform: "capitalize",
                  "&:disabled": {
                    backgroundColor: "#cccccc",
                  },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                {(mutationLoading || isNavigating) && (
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      border: "2px solid #ffffff",
                      borderTop: "2px solid transparent",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                )}
                {mutationLoading ? "Submitting..." : isNavigating ? "Creating..." : "Submit"}
              </Button>

              <Typography sx={{ fontSize: "14px", color: "#25385899", mt: 2 }}>
                Awaiting a response to an earlier query?{" "}
                <Link
                  to="/check-query"
                  state={{ bypassAuth: true }}
                  style={{ color: "#0065FF", fontWeight: 500 }}
                >
                  Track Existing Query
                </Link>
              </Typography>
            </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      <FAQSection />

      {/* File Preview Dialog */}
      <Dialog
        open={!!previewFileUrl}
        onClose={() => setPreviewFileUrl(null)}
        fullScreen
        PaperProps={{
          sx: {
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            background: 'rgba(30,30,30,0.96)',
            m: 0,
            p: 0,
            borderRadius: 0,
            position: 'relative',
          }
        }}
      >
        {/* Absolute X button at top right of viewport */}
        <IconButton
          aria-label="close"
          onClick={() => setPreviewFileUrl(null)}
          sx={{
            position: 'fixed',
            top: 16,
            right: 24,
            color: '#fff',
            zIndex: 2001,
            background: 'rgba(0,0,0,0.3)',
            '&:hover': { background: 'rgba(0,0,0,0.5)' },
            width: 44,
            height: 44,
          }}
        >
          <DeleteIcon sx={{ fontSize: 32 }} />
        </IconButton>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            p: 0,
            mt: 6, // margin from top
            background: 'none',
            width: '100vw',
            minWidth: 240,
            maxWidth: '100vw',
            height: '100vh',
            minHeight: 180,
            maxHeight: '100vh',
            boxSizing: 'border-box',
          }}
        >
          {previewFileUrl ? (
            <iframe
              src={previewFileUrl}
              title="File Preview"
              style={{
                width: '80vw',
                height: '75vh',
                border: 'none',
                borderRadius: 12,
                background: '#fff',
                boxShadow: '0 2px 24px rgba(0,0,0,0.35)'
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default TicketForm;
